import { createFileRoute } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import SafariDetailsPage from '../../pages/SafariDetailsPage';
import { getSafariDetail } from '../../server/getSafariDetail';
import { getHandPickedSafaris } from '../../server/getHandPickedSafaris';
import { destinationMiddleware } from '../../server/middleware/destinationMiddleware';
import { generateTouristTripSchema, generateBreadcrumbSchema } from '../../utils/jsonLd';

const loadDestination = createServerFn()
    .middleware([destinationMiddleware])
    .handler(async ({ context }) => {
        return context.destination;
    });

export const Route = createFileRoute('/safaris/$slug')({
    beforeLoad: async () => {
        try {
            const destination = await loadDestination();
            return { destination };
        } catch (error) {
            console.error('Failed to load destination:', error);
            throw error;
        }
    },
    loader: async ({ params, context }) => {
        const { slug } = params;
        const { destination } = context;

        const safariData = await getSafariDetail({ data: { slug } }).catch(() => []);
        const safari = safariData?.[0] || null;

        const destinationId = safari?.visibleOn?.destination || null;

        const handpickedSafaris = await getHandPickedSafaris({
            data: {
                destination: destination?._id || destinationId,
                limit: 3,
            },
        }).catch(() => []);

        return {
            safari,
            handpickedSafaris,
            destination,
        };
    },
    head: ({ loaderData, params }) => {
        const safari = loaderData?.safari;
        if (!safari) return {};

        const title = safari.metaData?.title || safari.title;
        const description = safari.metaData?.description || safari.briefDescription;
        const image = safari.photos?.[0]?.url;
        const siteUrl = (loaderData?.destination?.url || 'https://www.maasaimara.com').replace(/\/$/, '');
        const canonicalUrl = `${siteUrl}/safaris/${params.slug}`;

        const touristTripSchema = generateTouristTripSchema(safari, canonicalUrl);
        const breadcrumbSchema = generateBreadcrumbSchema(
            [
                { title: 'Home', url: '/' },
                { title: 'Safaris', url: '/safaris' },
                { title: safari.title, url: `/safaris/${params.slug}` },
            ],
            siteUrl
        );

        return {
            meta: [
                { title },
                { name: 'description', content: description },
                { property: 'og:title', content: title },
                { property: 'og:description', content: description },
                { property: 'og:type', content: 'website' },
                { property: 'og:url', content: canonicalUrl },
                ...(image
                    ? [
                        { property: 'og:image', content: image },
                        { property: 'og:image:type', content: 'image/jpeg' },
                    ]
                    : []),
            ],
            links: [
                { rel: 'canonical', href: canonicalUrl },
            ],
            scripts: [
                ...(touristTripSchema
                    ? [{ type: 'application/ld+json', children: JSON.stringify(touristTripSchema) }]
                    : []),
                ...(breadcrumbSchema
                    ? [{ type: 'application/ld+json', children: JSON.stringify(breadcrumbSchema) }]
                    : []),
            ],
        };
    },
    component: SafariDetailsPage,
    // ── Caching config 
    staleTime: 5 * 60 * 1000,   // reuse cached data for 5 mins on re-navigation
    gcTime: 10 * 60 * 1000,  // keep in memory for 10 mins after route unmounts
});